import type { ProductColumnMatchInfo } from '@Store/types/product'

import type { ConveyorTargetFieldMapping } from '../../../types/product'

/** A feed column as the preview/visit endpoint returns it, before mapping. */
type FeedColumn = ProductColumnMatchInfo

/** A column carrying the property it should start out mapped to, if any. */
export type FeedMappingRow = FeedColumn & { mappedName: string | null }

/**
 * Whether a column's sample value is a nested structure rather than a scalar.
 *
 * `typeof null` is `'object'`, so a bare `typeof value === 'object'` test also
 * catches every column whose first record happened to be null — which is a
 * perfectly mappable column with nothing in one record, not a nested object.
 */
const isStructuredValue = (value: unknown): boolean =>
  Array.isArray(value) || (typeof value === 'object' && value !== null)

/**
 * The sample values sent alongside the column names to the suggester.
 *
 * Null and undefined are left out rather than stringified: `"null"` is not a
 * sample of anything, and feeding it to a heuristic that reads a value's shape
 * ("a 13-digit number is a barcode") can only mislead it. The column still
 * gets a row from {@link buildFeedMappingRows} — it is only the hint that is
 * withheld, so the user maps it by hand or by its name.
 */
export const buildFeedSampleValues = (
  columns: FeedColumn[] = [],
): Record<string, string> =>
  columns.reduce<Record<string, string>>((acc, item) => {
    if (item?.value === null || item?.value === undefined) {
      return acc
    }

    if (isStructuredValue(item.value)) {
      return acc
    }

    acc[item.name] = String(item.value)
    return acc
  }, {})

/** Feed column name -> the property key it is already mapped to. */
export const buildPreviousMappingLookup = (
  previouslyMappedFields: ConveyorTargetFieldMapping[] = [],
): Record<string, string> =>
  previouslyMappedFields.reduce<Record<string, string>>((acc, field) => {
    const property = field?.conveyorField?.name
    if (field?.name && property) {
      acc[field.name] = property
    }
    return acc
  }, {})

/**
 * The mapping rows the dialog renders, one per mappable feed column.
 *
 * A mapping a human configured by hand outranks anything the suggester
 * proposes: the suggester only ever sees column names and sample values, so on
 * the edit path it cannot know what was saved and would happily overwrite it.
 * Suggestions therefore fill in only the columns that have no saved mapping —
 * which is also what makes a feed that grew new columns useful to reopen.
 */
export const buildFeedMappingRows = (
  columns: FeedColumn[] = [],
  {
    suggestions,
    previouslyMappedFields,
  }: {
    suggestions?: Record<string, string> | null
    previouslyMappedFields?: ConveyorTargetFieldMapping[]
  } = {},
): FeedMappingRow[] => {
  const previous = buildPreviousMappingLookup(previouslyMappedFields)

  return columns.reduce<FeedMappingRow[]>((acc, item) => {
    if (isStructuredValue(item?.value)) {
      return acc
    }

    acc.push({
      ...item,
      mappedName: previous[item?.name] || suggestions?.[item?.name] || null,
    })
    return acc
  }, [])
}
